<template>
  <section class="agent-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">Agent</p>
        <h1>Agent setup</h1>
        <p class="subtitle">
          Configure the AI receptionist your customers will talk to by phone,
          chatbot, and live demo.
        </p>
      </div>

      <div class="header-actions">
        <button class="ghost-btn" type="button" @click="reloadAgent" :disabled="loading">
          <RefreshCcw :size="16" :class="{ spin: loading }" />
          Reload
        </button>
        <button class="primary-btn" type="button" @click="submitAgent" :disabled="saving || loading">
          <Save :size="16" />
          {{ saving ? 'Saving...' : 'Save agent' }}
        </button>
      </div>
    </header>

    <div v-if="loading" class="loading-card">
      <div class="loader"></div>
      <h2>Loading active agent</h2>
      <p>Retrieving real agent data from the API.</p>
    </div>

    <div v-else-if="error" class="error-card">
      {{ error }}
    </div>

    <div v-else class="layout">
      <aside class="summary-card">
        <div class="agent-avatar" :style="{ backgroundColor: agent.primary_color || '#17211b' }">
          {{ initials }}
        </div>
        <h2>{{ agent.name || 'Unnamed agent' }}</h2>
        <p>{{ agent.business_name || 'Business name not set' }}</p>

        <div class="status-row">
          <span class="status" :class="{ active: agent.active }">
            {{ agent.active ? 'Active' : 'Inactive' }}
          </span>
          <span class="status" :class="{ active: agent.booking_enabled }">
            {{ agent.booking_enabled ? 'Booking on' : 'Booking off' }}
          </span>
        </div>

        <div class="summary-list">
          <div>
            <span>Industry</span>
            <strong>{{ industryLabel(agent.industry_key) }}</strong>
          </div>
          <div>
            <span>Language</span>
            <strong>{{ languageLabel(agent.default_language) }}</strong>
          </div>
          <div>
            <span>Agent ID</span>
            <strong>#{{ agent.id }}</strong>
          </div>
        </div>

        <div class="quick-actions">
          <router-link to="/app/test-live">Test live</router-link>
          <router-link to="/app/booking/settings">Booking settings</router-link>
          <router-link to="/app/telephony">Telephony</router-link>
        </div>
      </aside>

      <div class="settings-form">
        <AgentCoachPanel
          v-if="agent.id"
          :agent-id="agent.id"
          @applied="handleCoachApplied"
        />

        <form class="agent-edit-form" @submit.prevent="submitAgent">
          <section class="panel">
            <div class="panel-heading">
              <div>
                <h2>Business identity</h2>
                <p>Used in prompts, conversations, call handling, and widgets.</p>
              </div>
            </div>

            <div class="form-grid">
              <label>
                <span>Agent name</span>
                <input v-model.trim="agent.name" required placeholder="Downtown Dental Receptionist" />
              </label>

              <label>
                <span>Business name</span>
                <input v-model.trim="agent.business_name" placeholder="Downtown Dental" />
              </label>

              <label>
                <span>Industry</span>
                <select v-model="agent.industry_key">
                  <option value="generic">Generic service business</option>
                  <option value="dental_clinic">Dental clinic</option>
                  <option value="medical_clinic">Medical clinic</option>
                  <option value="law_firm">Law firm</option>
                  <option value="real_estate">Real estate</option>
                  <option value="home_services">Home services</option>
                  <option value="ecommerce">E-commerce</option>
                  <option value="saas">SaaS</option>
                </select>
              </label>

              <label>
                <span>Default language</span>
                <select v-model="agent.default_language">
                  <option v-for="option in LANGUAGE_OPTIONS" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </label>

              <label class="full">
                <span>Agent job description</span>
                <textarea
                  v-model.trim="agent.description"
                  rows="5"
                  placeholder="Explain what this agent should do, who it serves, and what outcomes matter."
                ></textarea>
              </label>
            </div>
          </section>

          <section class="panel">
            <div class="panel-heading">
              <div>
                <h2>Capabilities</h2>
                <p>Make the agent match the NoCodeAgent offer: calls, bookings, payments, and chatbot.</p>
              </div>
            </div>

            <div class="toggle-grid">
              <label class="toggle-card">
                <input type="checkbox" v-model="agent.active" />
                <div>
                  <strong>Agent active</strong>
                  <p>Allow this agent to be used in live tests and channels.</p>
                </div>
              </label>

              <label class="toggle-card">
                <input type="checkbox" v-model="agent.booking_enabled" />
                <div>
                  <strong>Booking enabled</strong>
                  <p>Let the AI collect date, time, service, name, and phone.</p>
                </div>
              </label>
            </div>

            <label class="field-block">
              <span>Allowed languages</span>
              <input
                v-model="allowedLanguagesText"
                placeholder="english, french"
              />
              <small>Separate with commas. The AI will answer in these languages when detected.</small>
            </label>
          </section>

          <section class="panel">
            <div class="panel-heading">
              <div>
                <h2>Brand and widget</h2>
                <p>Used by embedded chatbot and customer-facing agent interfaces.</p>
              </div>
            </div>

            <div class="form-grid">
              <label>
                <span>Primary color</span>
                <input v-model.trim="agent.primary_color" placeholder="#0d8f62" />
              </label>

              <label>
                <span>UI style</span>
                <select v-model="agent.ui_style">
                  <option value="">Default</option>
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                  <option value="minimal">Minimal</option>
                  <option value="modern">Modern</option>
                </select>
              </label>

              <label>
                <span>Font family</span>
                <input v-model.trim="agent.font_family" placeholder="Inter, system-ui" />
              </label>

              <label>
                <span>Voice style</span>
                <input v-model.trim="agent.voice_style" placeholder="Warm, fast, professional" />
              </label>

              <label class="full">
                <span>Logo URL</span>
                <input v-model.trim="agent.logo_url" type="url" placeholder="https://example.com/logo.png" />
              </label>
            </div>
          </section>

          <div class="form-actions">
            <p v-if="success" class="success-message">Agent saved successfully.</p>
            <button class="primary-btn" type="submit" :disabled="saving">
              <Save :size="16" />
              {{ saving ? 'Saving...' : 'Save changes' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import Cookies from 'js-cookie'
import { RefreshCcw, Save } from 'lucide-vue-next'
import * as api from '@/services/api.js'
import { LANGUAGE_OPTIONS, LANGUAGE_LABEL_MAP } from '@/constants/languages.js'
import AgentCoachPanel from '@/components/AgentCoachPanel.vue'

const router = useRouter()

const loading = ref(true)
const saving = ref(false)
const error = ref('')
const success = ref(false)
const allowedLanguagesText = ref('')

const agent = reactive({
  id: null,
  name: '',
  description: '',
  active: true,
  business_name: '',
  industry_key: 'generic',
  default_language: 'english',
  allowed_languages: '',
  booking_enabled: false,
  primary_color: '#0d8f62',
  font_family: '',
  logo_url: '',
  ui_style: '',
  voice_style: ''
})

const initials = computed(() => {
  return (agent.name || agent.business_name || 'AI')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('') || 'AI'
})

onMounted(reloadAgent)

async function reloadAgent() {
  loading.value = true
  error.value = ''
  success.value = false

  try {
    const token = Cookies.get('token')
    if (!token) {
      router.push('/sign-in')
      return
    }

    const activeAgentId = Cookies.get('active_agent_id')
    if (!activeAgentId) {
      router.push('/agents')
      return
    }

    const res = await api.agents.getById(activeAgentId)
    applyAgentData(res?.agent || res?.data || res)
  } catch (err) {
    error.value = err.message || 'Unable to load agent.'
  } finally {
    loading.value = false
  }
}

function applyAgentData(data) {
  if (!data) {
    error.value = 'Unable to load the selected agent.'
    return
  }

  Object.assign(agent, {
    id: data.id ?? null,
    name: data.name ?? '',
    description: data.description ?? '',
    active: toBoolean(data.active, true),
    business_name: data.business_name ?? '',
    industry_key: data.industry_key ?? 'generic',
    default_language: data.default_language ?? data.default_lang ?? 'english',
    allowed_languages: data.allowed_languages ?? '',
    booking_enabled: toBoolean(data.booking_enabled, false),
    primary_color: data.primary_color ?? '#0d8f62',
    font_family: data.font_family ?? '',
    logo_url: data.logo_url ?? '',
    ui_style: data.ui_style ?? '',
    voice_style: data.voice_style ?? ''
  })

  allowedLanguagesText.value = parseAllowedLanguages(agent.allowed_languages, agent.default_language)
}

async function submitAgent() {
  if (!agent.id) {
    error.value = 'No active agent selected.'
    return
  }

  saving.value = true
  error.value = ''
  success.value = false

  try {
    await api.agents.update(agent.id, {
      name: agent.name,
      description: agent.description,
      active: agent.active ? 1 : 0,
      business_name: agent.business_name || null,
      industry_key: agent.industry_key || 'generic',
      default_language: agent.default_language || 'english',
      allowed_languages: buildAllowedLanguagesPayload(),
      booking_enabled: agent.booking_enabled ? 1 : 0,
      primary_color: agent.primary_color || null,
      font_family: agent.font_family || null,
      logo_url: agent.logo_url || null,
      ui_style: agent.ui_style || null,
      voice_style: agent.voice_style || null
    })

    success.value = true
    window.setTimeout(() => {
      success.value = false
    }, 2500)
  } catch (err) {
    error.value = err.message || 'Unable to save agent.'
  } finally {
    saving.value = false
  }
}

async function handleCoachApplied() {
  await reloadAgent()
}

function buildAllowedLanguagesPayload() {
  const list = allowedLanguagesText.value
    .split(',')
    .map(item => item.trim().toLowerCase())
    .filter(Boolean)

  if (agent.default_language) {
    list.push(agent.default_language)
  }

  if (!list.length) list.push(agent.default_language || 'english')
  return JSON.stringify([...new Set(list)])
}

function parseAllowedLanguages(value, fallbackLanguage = 'english') {
  if (!value) return fallbackLanguage
  if (Array.isArray(value)) return value.join(', ')

  try {
    const parsed = JSON.parse(value)
    if (Array.isArray(parsed)) return parsed.join(', ')
  } catch (_) {
    return String(value)
  }

  return String(value)
}

function toBoolean(value, fallback = false) {
  if (value === undefined || value === null) return fallback
  return value === true || value === 1 || value === '1'
}

function industryLabel(value) {
  return {
    generic: 'Generic',
    dental_clinic: 'Dental clinic',
    medical_clinic: 'Medical clinic',
    law_firm: 'Law firm',
    real_estate: 'Real estate',
    home_services: 'Home services',
    ecommerce: 'E-commerce',
    saas: 'SaaS'
  }[value] || value
}

function languageLabel(value) {
  return LANGUAGE_LABEL_MAP[value] || value
}
</script>

<style scoped>
.agent-page{min-height:100vh;background:#f6f7fb;padding:32px;color:#17211b}.page-header{display:flex;align-items:flex-start;justify-content:space-between;gap:22px;margin-bottom:22px}.eyebrow{margin:0 0 8px;color:#0d8f62;font-size:12px;font-weight:900;letter-spacing:.08em;text-transform:uppercase}h1,h2,p{margin:0}h1{font-size:clamp(2rem,4vw,3.1rem);letter-spacing:-.04em}.subtitle{max-width:720px;margin-top:8px;color:#66736b;line-height:1.6}.header-actions{display:flex;gap:10px;flex-wrap:wrap}.primary-btn,.ghost-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:42px;border-radius:8px;padding:0 15px;border:none;font-weight:900;cursor:pointer}.primary-btn{background:#17211b;color:#fff}.ghost-btn{background:#fff;color:#17211b;border:1px solid #dfe7e1}.primary-btn:disabled,.ghost-btn:disabled{opacity:.6;cursor:not-allowed}.layout{display:grid;grid-template-columns:330px minmax(0,1fr);gap:20px;align-items:start}.summary-card,.panel,.loading-card,.error-card{background:#fff;border:1px solid #e5e7eb;border-radius:12px;box-shadow:0 18px 50px rgba(15,23,42,.05)}.summary-card{position:sticky;top:20px;padding:22px}.agent-avatar{width:62px;height:62px;display:flex;align-items:center;justify-content:center;border-radius:12px;color:#fff;font-weight:900;font-size:1.25rem}.summary-card h2{margin-top:16px;font-size:1.4rem}.summary-card p{margin-top:6px;color:#66736b}.status-row{display:flex;gap:8px;flex-wrap:wrap;margin-top:16px}.status{border-radius:999px;background:#eef2f0;color:#526159;padding:7px 10px;font-size:12px;font-weight:900}.status.active{background:#d1fae5;color:#047857}.summary-list{display:grid;gap:12px;margin-top:18px;padding-top:18px;border-top:1px solid #e6ece8}.summary-list span{display:block;color:#66736b;font-size:12px;font-weight:900;text-transform:uppercase}.summary-list strong{display:block;margin-top:4px}.quick-actions{display:grid;gap:9px;margin-top:20px}.quick-actions a{border:1px solid #dfe7e1;border-radius:8px;padding:11px 12px;color:#17211b;text-decoration:none;font-weight:900}.settings-form,.agent-edit-form{display:grid;gap:18px}.panel{padding:22px}.panel-heading{margin-bottom:18px}.panel-heading h2{font-size:1.3rem}.panel-heading p{margin-top:5px;color:#66736b}.form-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px}.form-grid label,.field-block{display:grid;gap:8px}.full{grid-column:1/-1}label span,.field-block span{color:#344238;font-size:12px;font-weight:900;text-transform:uppercase}input,textarea,select{width:100%;border:1px solid #d7e0da;border-radius:8px;background:#fff;color:#17211b;padding:12px 13px;font:inherit;outline:none}textarea{resize:vertical;line-height:1.5}input:focus,textarea:focus,select:focus{border-color:#0d8f62;box-shadow:0 0 0 4px rgba(13,143,98,.12)}.toggle-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:16px}.toggle-card{display:flex;align-items:flex-start;gap:12px;border:1px solid #e1e8e3;border-radius:10px;padding:14px;background:#fbfdfb}.toggle-card input{width:18px;height:18px;accent-color:#0d8f62}.toggle-card strong{display:block}.toggle-card p,.field-block small{margin-top:4px;color:#66736b;line-height:1.45}.form-actions{display:flex;align-items:center;justify-content:flex-end;gap:12px}.success-message{margin-right:auto;color:#047857;font-weight:900}.loading-card,.error-card{padding:34px;text-align:center}.error-card{color:#b91c1c;background:#fff7f7;border-color:#fecaca}.loader{width:34px;height:34px;margin:0 auto 16px;border:3px solid #e5e7eb;border-top-color:#17211b;border-radius:50%;animation:spin .8s linear infinite}.spin{animation:spin .8s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}@media(max-width:980px){.layout{grid-template-columns:1fr}.summary-card{position:static}.page-header{flex-direction:column}.header-actions{width:100%}.primary-btn,.ghost-btn{flex:1}}@media(max-width:640px){.agent-page{padding:18px 12px}.form-grid,.toggle-grid{grid-template-columns:1fr}.form-actions{flex-direction:column}.form-actions .primary-btn{width:100%}.success-message{margin-right:0}}
</style>
